import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Block,
  CheckCircle,
  CloudDownload,
  DeleteOutline,
  NoteAdd,
  Refresh,
  Visibility,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import FileSaver from "file-saver";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import KnowledgeSourceModal from "../KnowledgeSourceModal";
import KnowledgeReindexModal from "../KnowledgeReindexModal";
import { formatKnowledgeBytes, statusInfo } from "../../helpers/knowledgeManagement";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 18 },
  content: { display: "flex", flexDirection: "column", gap: theme.spacing(2), minHeight: 360 },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: theme.spacing(2) },
  meta: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), marginTop: theme.spacing(1) },
  tablePaper: { border: `1px solid ${theme.palette.divider}`, borderRadius: 14, overflow: "hidden", boxShadow: "none" },
  progress: { minWidth: 120 },
  actions: { display: "flex", justifyContent: "center" },
  empty: { padding: theme.spacing(4), textAlign: "center" },
  preview: { whiteSpace: "pre-wrap", fontFamily: "monospace", maxHeight: "60vh", overflow: "auto", padding: theme.spacing(2) },
  error: { color: theme.palette.error.main, maxWidth: 280 },
}));

const KnowledgeBaseDetail = ({ open, onClose, knowledgeBase, onEdit, onChanged, limits, profiles, credentials }) => {
  const classes = useStyles();
  const [detail, setDetail] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceModal, setSourceModal] = useState({ open: false, sourceId: null, sourceType: null, sourceUrl: null, sourceName: null });
  const [deleteSource, setDeleteSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reindexOpen, setReindexOpen] = useState(false);

  const load = useCallback(async () => {
    if (!open || !knowledgeBase?.id) return;
    setLoading(true);
    try {
      const [baseResponse, sourcesResponse] = await Promise.all([
        api.get(`/knowledge-bases/${knowledgeBase.id}`),
        api.get(`/knowledge-bases/${knowledgeBase.id}/sources`, { params: { pageSize: 50 } }),
      ]);
      setDetail(baseResponse.data);
      setSources(sourcesResponse.data.sources || []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [knowledgeBase, open]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const hasWork = detail?.reindex?.status === "processing"
      || sources.some((source) => ["queued", "processing"].includes(source.latestVersion?.latestIndexRun?.status));
    if (!open || !hasWork) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [detail, load, open, sources]);

  const mutate = async (source, action, success) => {
    try {
      await api.post(`/knowledge-bases/${knowledgeBase.id}/sources/${source.id}/${action}`);
      toast.success(success);
      await load();
      onChanged();
    } catch (error) {
      toastError(error);
    }
  };

  const remove = async () => {
    if (!deleteSource) return;
    try {
      await api.delete(`/knowledge-bases/${knowledgeBase.id}/sources/${deleteSource.id}`);
      toast.success("Exclusão física agendada conforme a retenção configurada.");
      setDeleteSource(null);
      await load();
      onChanged();
    } catch (error) {
      toastError(error);
    }
  };

  const download = async (version) => {
    try {
      const response = await api.get(version.originalDownloadUrl, { responseType: "blob" });
      FileSaver.saveAs(response.data, version.originalFileName || "documento");
    } catch (error) {
      toastError(error);
    }
  };

  const showExtracted = async (version) => {
    try {
      const { data } = await api.get(version.extractedContentUrl);
      setPreview(data);
    } catch (error) {
      toastError(error);
    }
  };

  const closeSourceModal = () => setSourceModal({ open: false, sourceId: null, sourceType: null, sourceUrl: null, sourceName: null });

  const approveVersion = async (source, version) => {
    try {
      await api.post(`/knowledge-bases/${knowledgeBase.id}/sources/${source.id}/versions/${version.id}/approve`);
      toast.success("Revisão aprovada e ativada.");
      await load();
      onChanged();
    } catch (error) {
      toastError(error);
    }
  };

  const approveReindex = async () => {
    try {
      await api.post("/knowledge-bases/reindex/approve", { knowledgeBaseIds: [knowledgeBase.id] });
      toast.success("Nova geração aprovada e ativada.");
      await load();
      onChanged();
    } catch (error) {
      toastError(error);
    }
  };

  const retryReindex = async () => {
    try {
      await api.post(`/knowledge-bases/${knowledgeBase.id}/reindex/retry`);
      toast.success("Reindexação reenfileirada.");
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <>
      <ConfirmationModal
        title={deleteSource ? `Excluir ${deleteSource.name}?` : "Excluir fonte"}
        open={Boolean(deleteSource)}
        onClose={() => setDeleteSource(null)}
        onConfirm={remove}
      >
        A fonte sai da busca imediatamente. Os arquivos serão removidos de forma assíncrona após o prazo de retenção.
      </ConfirmationModal>
      <KnowledgeSourceModal
        open={sourceModal.open}
        onClose={closeSourceModal}
        onSaved={async () => { closeSourceModal(); await load(); onChanged(); }}
        knowledgeBaseId={knowledgeBase?.id}
        sourceId={sourceModal.sourceId}
        sourceType={sourceModal.sourceType}
        sourceUrl={sourceModal.sourceUrl}
        sourceName={sourceModal.sourceName}
        limits={limits}
      />
      <KnowledgeReindexModal
        open={reindexOpen}
        onClose={() => setReindexOpen(false)}
        onStarted={async () => { setReindexOpen(false); await load(); onChanged(); }}
        knowledgeBase={detail || knowledgeBase}
        profiles={profiles || []}
        credentials={credentials || []}
      />
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>Conteúdo extraído · revisão {preview?.version}</DialogTitle>
        <DialogContent dividers><Paper variant="outlined" className={classes.preview}>{preview?.content}</Paper></DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Fechar</Button></DialogActions>
      </Dialog>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth classes={{ paper: classes.paper }}>
        <DialogTitle>Base de conhecimento</DialogTitle>
        <DialogContent dividers>
          <div className={classes.content}>
            {loading && !detail ? <Box textAlign="center" p={6}><CircularProgress /></Box> : (
              <>
                <div className={classes.header}>
                  <Box>
                    <Typography variant="h6">{detail?.name || knowledgeBase?.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{detail?.description || "Sem descrição"}</Typography>
                    <div className={classes.meta}>
                      <Chip size="small" label={detail?.embeddingProfile?.code || "Perfil"} />
                      <Chip size="small" label={`${detail?.embeddingProfile?.dimension || 0} dimensões`} />
                      <Chip size="small" label={`${detail?.agents?.length || 0} agentes vinculados`} />
                    </div>
                  </Box>
                  <Box display="flex" gridGap={8}>
                    <Button variant="outlined" onClick={() => onEdit(detail || knowledgeBase)}>Editar base</Button>
                    <Button variant="outlined" onClick={() => setReindexOpen(true)} disabled={Boolean(detail?.reindex)}>Reindexar</Button>
                    <Button color="primary" variant="contained" onClick={() => setSourceModal({ open: true, sourceId: null })}>Adicionar fonte</Button>
                  </Box>
                </div>

                {detail?.reindex && (
                  <Paper variant="outlined" style={{ padding: 14 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" gridGap={12}>
                      <Box flex={1}>
                        <Typography variant="subtitle2">Nova geração de embeddings</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {detail.pendingEmbeddingProfile?.code || "Novo perfil"} · {detail.reindex.completedRuns} de {detail.reindex.totalRuns} fontes processadas · {detail.reindex.chunkCount || 0} trechos
                        </Typography>
                        <LinearProgress variant="determinate" value={detail.reindex.progress || 0} />
                        {detail.reindex.errorMessage && <Typography variant="caption" display="block" className={classes.error}>{detail.reindex.errorMessage}</Typography>}
                      </Box>
                      {detail.reindex.status === "ready_for_approval" && <Button color="primary" variant="contained" onClick={approveReindex}>Aprovar geração</Button>}
                      {detail.reindex.status === "failed" && <Button variant="outlined" onClick={retryReindex}>Tentar novamente</Button>}
                    </Box>
                  </Paper>
                )}

                {Boolean(detail?.agents?.length) && (
                  <Paper variant="outlined" style={{ padding: 12 }}>
                    <Typography variant="subtitle2">Agentes afetados por mudanças nesta base</Typography>
                    <Box display="flex" flexWrap="wrap" gridGap={6} mt={1}>
                      {detail.agents.map((agent) => <Chip key={agent.id} size="small" label={agent.name} />)}
                    </Box>
                  </Paper>
                )}

                <Paper className={classes.tablePaper}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>Fonte</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Revisão</TableCell>
                        <TableCell>Tamanho</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {sources.map((source) => {
                          const version = source.latestVersion || source.activeVersion;
                          const run = version?.latestIndexRun;
                          const effectiveStatus = run && run.status !== "approved" ? run.status : source.status;
                          const status = statusInfo(effectiveStatus, source.processingProgress || version?.processingProgress);
                          const processing = ["uploaded", "queued", "processing"].includes(effectiveStatus);
                          return (
                            <TableRow key={source.id} hover>
                              <TableCell>
                                <Typography variant="body2"><strong>{source.name}</strong></Typography>
                                <Typography variant="caption" color="textSecondary">{source.type.toUpperCase()} · {source.versionCount || 0} revisões</Typography>
                                {source.type === "url" && version?.inputMetadata?.sourceUrl && (
                                  <Typography variant="caption" display="block" color="textSecondary">
                                    <a href={version.inputMetadata.finalUrl || version.inputMetadata.sourceUrl} target="_blank" rel="noopener noreferrer">Abrir página original</a>
                                    {version.inputMetadata.capturedAt && ` · capturada em ${new Date(version.inputMetadata.capturedAt).toLocaleString("pt-BR")}`}
                                  </Typography>
                                )}
                                {source.errorMessage && <Typography variant="caption" display="block" className={classes.error}>{source.errorMessage}</Typography>}
                                {run && <Typography variant="caption" display="block" color="textSecondary">{run.provider}/{run.model} · geração {run.indexGeneration} · {run.chunkCount || 0} trechos · {run.requestCount || 0} chamadas</Typography>}
                                {run?.errorMessage && <Typography variant="caption" display="block" className={classes.error}>{run.errorMessage}</Typography>}
                              </TableCell>
                              <TableCell>
                                <Chip size="small" color={status.color} label={status.label} />
                                {processing && <LinearProgress className={classes.progress} variant="determinate" value={Math.min(100, status.progress)} />}
                              </TableCell>
                              <TableCell>{version ? `v${version.version}` : "—"}</TableCell>
                              <TableCell>{formatKnowledgeBytes(version?.sizeBytes)}</TableCell>
                              <TableCell align="center">
                                <div className={classes.actions}>
                                  <Tooltip title="Baixar original"><span><IconButton aria-label={`Baixar original de ${source.name}`} size="small" disabled={!version} onClick={() => download(version)}><CloudDownload /></IconButton></span></Tooltip>
                                  <Tooltip title="Conferir conteúdo extraído"><span><IconButton aria-label={`Conferir conteúdo extraído de ${source.name}`} size="small" disabled={!version?.extractionChecksum} onClick={() => showExtracted(version)}><Visibility /></IconButton></span></Tooltip>
                                  <Tooltip title={source.type === "url" ? "Atualizar página" : "Adicionar nova revisão"}><span><IconButton aria-label={`${source.type === "url" ? "Atualizar página" : "Adicionar nova revisão"} de ${source.name}`} size="small" disabled={["deleting", "deleted"].includes(source.status)} onClick={() => setSourceModal({ open: true, sourceId: source.id, sourceType: source.type, sourceUrl: version?.inputMetadata?.sourceUrl, sourceName: source.name })}><NoteAdd /></IconButton></span></Tooltip>
                                  <Tooltip title="Aprovar e ativar esta geração"><span><IconButton aria-label={`Aprovar ${source.name}`} size="small" disabled={run?.status !== "completed"} onClick={() => approveVersion(source, version)}><CheckCircle /></IconButton></span></Tooltip>
                                  <Tooltip title="Reprocessar"><span><IconButton aria-label={`Reprocessar ${source.name}`} size="small" disabled={["deleting", "deleted", "retired"].includes(source.status)} onClick={() => mutate(source, "reprocess", "Reprocessamento enfileirado.")}><Refresh /></IconButton></span></Tooltip>
                                  <Tooltip title="Retirar de uso"><span><IconButton aria-label={`Retirar ${source.name} de uso`} size="small" disabled={["retired", "deleting", "deleted"].includes(source.status)} onClick={() => mutate(source, "retire", "Fonte retirada das próximas buscas.")}><Block /></IconButton></span></Tooltip>
                                  <Tooltip title="Excluir"><span><IconButton aria-label={`Excluir ${source.name}`} size="small" disabled={["deleting", "deleted"].includes(source.status)} onClick={() => setDeleteSource(source)}><DeleteOutline /></IconButton></span></Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {!loading && !sources.length && (
                    <Box className={classes.empty}>
                      <Typography variant="subtitle1">Nenhuma fonte adicionada</Typography>
                      <Typography variant="body2" color="textSecondary">Adicione PDF textual, TXT, Markdown, texto digitado, perguntas e respostas ou uma página pública.</Typography>
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
      </Dialog>
    </>
  );
};

export default KnowledgeBaseDetail;
